import type { List, ListCreate, ListUpdate } from '../types/list'
import type { Elemental } from '../types/elemental'
import type { APIIntervals, APIOrderings } from '../types/api'
import type { Prompt } from '../types/prompt'
import type { GetUserPromptMode } from './users'
import type { ElementalQuery } from './elementals'
import { APIFetch, filterResponse, filterResponseBoolean } from '.'
export interface PageQuery {
  page?: number
  size?: number
  order_dir?: 'asc' | 'desc'
  order_by?: APIOrderings
  interval?: APIIntervals
  search?: string
  is_template?: boolean
}
export interface TreeQuery {
  page?: number
  size?: number
}

export interface GetListsQuery extends PageQuery {
  topic_id?: number | string
  prompt_id?: string
  mode?: GetUserPromptMode
  is_premium?: boolean
  companiesSection?: boolean
  elemental_type_id?: number
}

export const getLists = async (queries?: GetListsQuery): Promise<null | List[]> => {
  let url = '/elementals'

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const createList = async (body: ListCreate): Promise<null | List> => {
  return filterResponse(await APIFetch('/elementals', {
    method: 'POST',
    body,
  }))
}

export const getList = async (listId: string): Promise<null | List> => {
  return filterResponse(await APIFetch(`/elementals/${listId}`, {
    method: 'GET',
  }))
}

// Returns true if success
export const updateList = async (listId: string, body: ListUpdate): Promise<null | List> => {
  return filterResponse(await APIFetch(`/elementals/${listId}`, {
    method: 'PATCH',
    body,
  }))
}

// Returns true if success
export const deleteList = async (listId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/elementals/${listId}`, {
    method: 'DELETE',
  }))
}

export const uploadAvatar = async (listId: string, avatar: File, isAdmin?: boolean) => {
  const formData = new FormData()
  formData.append('file', avatar)

  return filterResponseBoolean(await APIFetch(`${isAdmin ? '/admin' : ''}/elementals/${listId}/avatar`, {
    method: 'PATCH',
    body: formData,
  }))
}

export const getListPrompts = async <T = Prompt>(
  listId: string,
  queries?: ElementalQuery & {
    mode?: string
    is_preview?: boolean
  },
  signal?: AbortSignal,
  isAdmin?: boolean,
): Promise<null | T[]> => {
  let url = `${isAdmin ? '/admin' : ''}/elementals/${listId}/items`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const getListItems = async <T = Elemental>(
  listId: string,
  queries?: PageQuery & {
    mode?: string
    is_preview?: boolean
    item_type?: 'all' | 'list' | 'prompt' | 'folder' | 'no-folder'
  },
  signal?: AbortSignal,
  isAdmin?: boolean,
): Promise<null | T[]> => {
  let url = `${isAdmin ? '/admin' : ''}/elementals/${listId}/items`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const getListTree = async <T = Elemental>(
  listId: string,
  queries?: TreeQuery,
  signal?: AbortSignal,
): Promise<null | T[]> => {
  let url = `/elementals/${listId}/tree`

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const addListPrompt = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/elementals/${listId}/items/${promptId}`, {
    method: 'POST',
  }))
}

export const removeListPrompt = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/elementals/${listId}/items/${promptId}`, {
    method: 'DELETE',
  }))
}

export const getUserLists = async (userId: string, queries?: GetListsQuery, signal?: AbortSignal): Promise<List[] | null> => {
  let url = `/users/${userId}/elementals`

  if (queries)
    url = useParseQuery(url, { ...queries, elemental_type_id: 0 })

  return filterResponse(await APIFetch(url, {
    method: 'GET',
    signal,
  }))
}

export const savePromptToList = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/elementals/${listId}/items/${promptId}`, {
    method: 'POST',
  }))
}
export const removePromptFromList = async (listId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/elementals/${listId}/items/${promptId}`, {
    method: 'DELETE',
  }))
}
